import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/Button/Button";
import DataTable from "@/components/Datatable/Datatable";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import { useNotification } from "@/context/NotificationContext";
import { useAppDispatch } from "@/app/hooks";
import { useLibraryModuleList } from "@/modules/learningCenter/features/ModuleLibrary/hooks/useLibraryModuleList/useLibraryModuleList";
import { createLibraryModuleColumns } from "@/modules/learningCenter/features/ModuleLibrary/components/LibraryModuleColumns/LibraryModuleColumns";
import LibraryModuleFilters from "@/modules/learningCenter/features/ModuleLibrary/components/LibraryModuleFilters/LibraryModuleFilters";
import {
  publishLibraryModuleAction,
  archiveLibraryModuleAction,
} from "@/modules/learningCenter/features/ModuleLibrary/slices/libraryModuleListSlice";
import { LibraryModuleListItem } from "@/modules/learningCenter/features/ModuleLibrary/types/libraryModule";

const LibraryModuleList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  const {
    modules,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    refetch,
    handleFilterChange,
    handlePaginationChange,
    handleSortingChange,
  } = useLibraryModuleList();

  const [moduleToArchive, setModuleToArchive] =
    useState<LibraryModuleListItem | null>(null);

  const handleCreateModule = useCallback(() => {
    navigate("/admin/learning-center/module-library/new");
  }, [navigate]);

  const handleEditModule = useCallback(
    (uuid: string) => {
      navigate(`/admin/learning-center/module-library/${uuid}/edit`);
    },
    [navigate],
  );

  const handlePublishModule = useCallback(
    (module: LibraryModuleListItem) => {
      dispatch(
        publishLibraryModuleAction(module.uuid, () => {
          showNotification(
            "Module published",
            `"${module.title}" is now published.`,
            "success",
          );
          refetch();
        }),
      );
    },
    [dispatch, showNotification, refetch],
  );

  const handleArchiveModule = useCallback((module: LibraryModuleListItem) => {
    setModuleToArchive(module);
  }, []);

  const handleConfirmArchive = useCallback(() => {
    if (!moduleToArchive) {
      return;
    }
    const module = moduleToArchive;
    dispatch(
      archiveLibraryModuleAction(module.uuid, () => {
        showNotification(
          "Module archived",
          `"${module.title}" has been archived.`,
          "success",
        );
        setModuleToArchive(null);
        refetch();
      }),
    );
  }, [moduleToArchive, dispatch, showNotification, refetch]);

  const columns = useMemo(
    () =>
      createLibraryModuleColumns({
        onEditModule: handleEditModule,
        onPublishModule: handlePublishModule,
        onArchiveModule: handleArchiveModule,
      }),
    [handleEditModule, handlePublishModule, handleArchiveModule],
  );

  return (
    <>
      <MainPageWrapper
        actions={<Button onClick={handleCreateModule}>Create Module</Button>}
        error={error}
        subtitle="Build, publish, and archive reusable modules."
        title="Module Library"
      >
        <LibraryModuleFilters
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        <DataTable<LibraryModuleListItem>
          columns={columns}
          data={modules}
          error={error}
          loading={loading}
          noDataMessage="No modules found"
          onPageChange={(newPageIndex, newPageSize) =>
            handlePaginationChange({
              pageIndex: newPageIndex,
              pageSize: newPageSize,
            })
          }
          onSortingChange={handleSortingChange}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowKeyExtractor={(item) => item.uuid}
          sorting={sorting}
          totalCount={totalCount}
        />
      </MainPageWrapper>

      <ConfirmationDialog
        cancelMessage="Cancel"
        confirmMessage="Archive"
        dialogInstructionFinalQuestion="Are you sure you want to archive this module?"
        dialogInstructionItems={[
          "The module will no longer be editable.",
          "It will be hidden from the published-module picker.",
        ]}
        dialogInstructionTitle="Archiving a library module:"
        dialogTitleContent={`Archive "${moduleToArchive?.title ?? ""}"?`}
        isOpen={moduleToArchive !== null}
        onClose={() => setModuleToArchive(null)}
        onConfirm={handleConfirmArchive}
      />
    </>
  );
};

export default LibraryModuleList;
